import React, { createContext, useContext, useState, useEffect } from 'react';
import { useCurrency } from './CurrencyContext';

const QuoteContext = createContext(null);

export const QuoteProvider = ({ children }) => {
  const { rate } = useCurrency();
  const [quoteItems, setQuoteItems] = useState(() => {
    const saved = localStorage.getItem('quoteItems');
    return saved ? JSON.parse(saved) : [];
  });

  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('cartItems');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('quoteItems', JSON.stringify(quoteItems));
  }, [quoteItems]);

  useEffect(() => {
    localStorage.setItem('cartItems', JSON.stringify(cartItems));
  }, [cartItems]);

  // ===== CART (Buy Now — products with fixed price) =====
  const addToCart = (product, quantity = 1) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.product_id === product.product_id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, {
        product_id: product.product_id,
        product_name: product.name_en,
        product_name_pt: product.name_pt,
        price_zar: product.price_zar,
        price_mzn: product.price_mzn || product.price_zar * rate,
        image: product.images?.[0],
        quantity,
        unit: product.unit || 'unit',
        min_quantity: product.min_quantity || 1,
        weight_kg: product.weight_kg || 0,
      }];
    });
  };

  const removeFromCart = (productId) => {
    setCartItems(prev => prev.filter(item => item.product_id !== productId));
  };

  const updateCartQuantity = (productId, quantity) => {
    if (quantity <= 0) { removeFromCart(productId); return; }
    setCartItems(prev => prev.map(item =>
      item.product_id === productId ? { ...item, quantity } : item
    ));
  };

  const clearCart = () => setCartItems([]);

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price_zar * item.quantity), 0);
  };

  const getCartTotalMZN = () => {
    return cartItems.reduce((total, item) => total + (item.price_mzn * item.quantity), 0);
  };

  const isInCart = (productId) => cartItems.some(item => item.product_id === productId);

  // ===== QUOTE (RFQ — products without fixed price) =====
  const addToQuote = (product, quantity = 1) => {
    setQuoteItems(prev => {
      const existing = prev.find(item => item.product_id === product.product_id);
      if (existing) {
        return prev.map(item =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, {
        product_id: product.product_id,
        product_name: product.name_en,
        product_name_pt: product.name_pt,
        price_zar: product.price_zar,
        price_mzn: product.price_mzn,
        image: product.images?.[0],
        quantity,
        unit: product.unit,
        min_quantity: product.min_quantity,
        is_quote_only: product.is_quote_only,
      }];
    });
  };

  const removeFromQuote = (productId) => {
    setQuoteItems(prev => prev.filter(item => item.product_id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) { removeFromQuote(productId); return; }
    setQuoteItems(prev => prev.map(item =>
      item.product_id === productId ? { ...item, quantity } : item
    ));
  };

  const clearQuote = () => setQuoteItems([]);

  const getQuoteTotal = () => {
    return quoteItems.reduce((total, item) => {
      if (item.is_quote_only) return total;
      return total + (item.price_zar * item.quantity);
    }, 0);
  };

  // Total item count (cart + quote)
  const getItemCount = () => {
    const cartCount = cartItems.reduce((c, item) => c + item.quantity, 0);
    const quoteCount = quoteItems.reduce((c, item) => c + item.quantity, 0);
    return cartCount + quoteCount;
  };

  const getCartItemCount = () => cartItems.reduce((c, item) => c + item.quantity, 0);

  const isInQuote = (productId) => quoteItems.some(item => item.product_id === productId);

  const value = {
    // Cart
    cartItems, addToCart, removeFromCart, updateCartQuantity, clearCart,
    getCartTotal, getCartTotalMZN, getCartItemCount, isInCart,
    // Quote
    quoteItems, addToQuote, removeFromQuote, updateQuantity, clearQuote,
    getQuoteTotal, isInQuote,
    // Combined
    getItemCount,
  };

  return <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>;
};

export const useQuote = () => {
  const context = useContext(QuoteContext);
  if (!context) throw new Error('useQuote must be used within a QuoteProvider');
  return context;
};
